import { FormField, TextInput } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import type { Aggregator } from "@/lib/api/types";

/** The server's bound on a platform's order number (`PLATFORM_ORDER_REF_MAX_LENGTH`). */
export const PLATFORM_ORDER_REF_MAX = 64;

/**
 * WHAT A PLATFORM BRINGS WITH IT (T13, T14) — rendered under the chips the
 * moment one is picked, on both dialogs:
 *
 * · the platform's own ORDER NUMBER — what the rider at the door and the
 *   customer on the phone both quote, and what the history search finds a
 *   bill by. Optional, 64 characters, meaningless (and dropped) without a
 *   platform;
 * · one line saying the SERVICE CHARGE IS OFF on a platform bill (T14): the
 *   server zeroes it whatever the channel flag says, and a cashier watching
 *   the total drop when a platform chip is pressed must be told why.
 *
 * Nothing when no platform is picked: a direct order has neither fact.
 */
export function PlatformOrderFields({
  visible,
  platformOrderRef,
  onChange,
  disabled,
  testIdPrefix,
}: {
  visible: boolean;
  platformOrderRef: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  testIdPrefix: string;
}) {
  const t = useTranslate();
  if (!visible) return null;
  return (
    <>
      <FormField
        name="platformOrderRef"
        label={t((m) => m.pos.platformOrderRefLabel)}
      >
        <TextInput
          name="platformOrderRef"
          value={platformOrderRef}
          maxLength={PLATFORM_ORDER_REF_MAX}
          // A reference is read back to a rider digit by digit; LTR keeps a
          // mixed "TLB-4821" in the order it was typed, whatever the UI's.
          dir="ltr"
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          data-testid={`${testIdPrefix}platform-ref`}
        />
      </FormField>
      <p
        className="text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)]"
        data-testid={`${testIdPrefix}platform-no-service`}
      >
        {t((m) => m.pos.platformNoServiceCharge)}
      </p>
    </>
  );
}

/**
 * WHO BROUGHT THE CUSTOMER (F2) — the platform picker, as chips.
 *
 * One component for the two dialogs that ask the question: `OffPremiseDialog`
 * at open and, since T6 (2026-10-06), `ChargesDialog` on an OPEN bill. The
 * picker reaches money — the chosen platform's rate is snapshotted onto the
 * bill — so the two screens must offer the same list, in the same order, with
 * the same words, or a cashier corrects a bill against a picker that does not
 * look like the one the order was taken on.
 *
 * Chips, not a dropdown: this is a short, stable list picked on a touch screen
 * mid-service, which is the same shape the tier picker answers the same way.
 * DIRECT leads and starts selected, because most stores take most orders on
 * their own phone and a picker with no default would make every direct order a
 * field to dismiss.
 *
 * Rendered only when the store HAS a platform. A lone "direct" chip is a
 * question with one answer, and a store that uses no aggregator should never
 * see the concept at all. `choices` is the caller's list — already filtered to
 * the ACTIVE platforms, plus (on a correction) the one the bill is currently on
 * even if it has since been retired, so the dialog cannot silently move a bill
 * off a platform the cashier never touched.
 */
export function AggregatorChips({
  choices,
  value,
  onChange,
  disabled,
  testIdPrefix,
}: {
  choices: Pick<Aggregator, "id" | "name" | "commissionPercent">[];
  /** `null` is DIRECT. */
  value: string | null;
  onChange: (next: string | null) => void;
  disabled?: boolean;
  /** `off-premise-aggregator-` on the open dialog, `charges-aggregator-` on the correction. */
  testIdPrefix: string;
}) {
  const t = useTranslate();
  if (choices.length === 0) return null;
  return (
    <div>
      <span className="mb-1.5 block text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.06em] text-[color:var(--text-disabled)]">
        {t((m) => m.pos.offPremiseAggregatorLabel)}
      </span>
      <div
        className="flex flex-wrap gap-1.5"
        role="group"
        aria-label={t((m) => m.pos.offPremiseAggregatorLabel)}
      >
        {[null, ...choices].map((option) => {
          const isCurrent = (option?.id ?? null) === value;
          return (
            <button
              key={option?.id ?? "direct"}
              type="button"
              aria-pressed={isCurrent}
              disabled={disabled}
              data-testid={`${testIdPrefix}${option?.id ?? "direct"}`}
              onClick={() => onChange(option?.id ?? null)}
              className={[
                "rounded-[var(--radius-control)] border-[length:var(--border-panel-width,1px)] px-2 py-[5px] text-[length:var(--font-size-2xs)] font-extrabold transition-colors",
                isCurrent
                  ? "border-[color:var(--accent)] bg-[color:var(--accent)] text-[color:var(--accent-contrast)]"
                  : "border-[color:var(--border-strong)] text-[color:var(--text-muted)] hover:text-[color:var(--text)]",
              ].join(" ")}
            >
              {option ? (
                // ISOLATED name, chip's own alignment — the shape
                // `SuppliersPage` measured its way to. "elmenus" beside
                // «طلبات» must not drag its rate to the other end of the chip.
                <span className="flex items-center gap-1">
                  <bdi>{option.name}</bdi>
                  {/* `dir="ltr"` on the SIGNED figure. Measured, not supposed:
                      without it the painted glyph order in the Arabic UI is
                      `15%−elmenus` and with it `−15%elmenus` — a leading
                      minus is weak-directional, so it gets carried to the far
                      end of the number run and the rate reads backwards. The
                      same rule the app's other signed figures already follow. */}
                  <span className="tabular-nums opacity-80" dir="ltr">
                    −{option.commissionPercent}%
                  </span>
                </span>
              ) : (
                t((m) => m.pos.offPremiseAggregatorDirect)
              )}
            </button>
          );
        })}
      </div>
      {/* What choosing one DOES, said once — and UX-round D37 · ruling F2:
          said when one has been chosen.

          The sentence describes what a PLATFORM does to the money: the rate
          comes off the night's takings and the settlement never reaches the
          drawer. On «مباشر», which is the default and the majority of bills,
          there is no platform, no commission and no settlement, so the
          paragraph explained a mechanism that was not running — printed under
          the chips before anybody had touched them.

          It is a gate on the EXPLANATION only. Nothing about the choice itself
          is hidden: the chips, the platform names and each one's rate are all
          on the canvas unconditionally, and the hint appears the instant a
          platform is pressed, which is the instant it becomes true. */}
      {value !== null ? (
        <p
          className="mt-1.5 text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)]"
          data-testid={`${testIdPrefix}hint`}
        >
          {t((m) => m.pos.offPremiseAggregatorHint)}
        </p>
      ) : null}
    </div>
  );
}
